import {
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Close,
  HelpOutline,
  RotateRight,
  Update,
  ZoomIn,
  ZoomOut,
  CameraAlt,
  Add,
} from '@mui/icons-material'
import {
  CircularProgress,
  Skeleton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { ExpandMore } from '@mui/icons-material'

import ConfirmChangesModal from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayVisualization/Modals/ConfirmChangesModal/ConfirmChangesModal'
import { LoadingButton } from 'components/mui'
import ViewImageDialog from 'components/organisms/ViewImageDialog/ViewImageDialog'

import useEditTray from './EditTray.logic'

import './EditTray.scss'
import { ConfirmLoadMultipleScrewsModal } from 'components/organisms/ConfirmLoadMultipleScrewsModal/ConfirmLoadMultipleScrewsModal'
import { BackOrButton } from 'components/BackOrButton'
import TrayProduct from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayProducts/TrayProduct/TrayProduct'
import TrayMapSelector from 'components/organisms/TrayMapSelector/TrayMapSelector'
import { LoadMultipleScrewsBox } from 'components/organisms/LoadMultipleScrewsBox/LoadMultipleScrewsBox'
import classNames from 'classnames'
import ReferenceImageAccurateModal from './ReferenceImageAccurateModal'
import OtherTrayItemsContextProvider from 'components/organisms/OtherTrayItems/OtherTrayItemsContextProvider'
import { OtherItemsTriggerButton } from 'components/organisms/OtherTrayItems/OtherTrayItemsTriggerButton'
import CustomImplantModal from 'components/organisms/CustomImplantModal/CustomImplantModal'

const EditTray = ({ isReviewing }: { isReviewing: boolean }) => {
  const {
    isInEditMode,
    handleEditModeChange,
    loading,
    error,
    subTrayItem,
    trayDetails,
    isConfirmationOpen,
    handleConfirmationClick,
    handleLoadWithUDIsClick,
    isLoadingWithUDIs,
    isAdditionalDeviceCountModalOpen,
    isRejecting,
    handleUserConfirmation,
    handleConfirmationClose,
    isUpdating,
    renderSubTrayPlates,
    isLoadMultipleBoxVisible,
    isInstrumentTray,
    isCustomImplantTray,
    hasPlates,
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
    hasScrews,
    isPlatesOnly,
    totalPlateCount,
    totalScrews,
    imageScale,
    setImageScale,
    imageRotation,
    setImageRotation,
    isLegendOpen,
    setIsLegendOpen,
    // Image history related
    currentImageIndex,
    imageHistory,
    isViewingHistory,
    hasMultipleImages,
    goToPreviousImage,
    goToNextImage,
    goToLatestImage,
    trayImage,
    isImageLoading,
    formattedTimestamp,
    referenceImageTitle,
    isImagePreviewOpen,
    setIsImagePreviewOpen,
    handleCaptureReferenceImage,
    isReferenceImageAccurateModalOpen,
    setIsReferenceImageAccurateModalOpen,
    handleReferenceImageAccurate,
    handleCaptureNewReferenceImage,
    otherItems,
    setOtherItems,
    isCustomImplantModalOpen,
    handleAddCustomImplantTrigger,
  } = useEditTray()
  const { trayId } = useParams<{ trayId: string }>()

  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(prev + 0.5, 3))
  }

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(prev - 0.5, 0.5))
  }

  const handleRotateImage = () => {
    setImageRotation((prev) => (prev + 90) % 360)
  }

  return (
    <OtherTrayItemsContextProvider
      otherItems={otherItems}
      setOtherItems={setOtherItems}
    >
      <div className="tray-procedure-edit-mode-organism_wrapper edit-tray-wrapper">
        <LoadMultipleScrewsBox
          open={isLoadMultipleBoxVisible}
          trayType={trayDetails?.trayType || 'stryker screw caddy'}
        />

        <div className="header contained-desktop">
          <div className="header_actions">
            <BackOrButton
              variant="text"
              className="back_button"
              sx={{ width: 'auto' }}
              size="small"
              fallbackUrl={`/tray-management/${trayId}`}
              fallbackChildren={
                <>
                  <ArrowBack sx={{ mr: 0.5 }} /> BACK
                </>
              }
            >
              <ArrowBack sx={{ mr: 0.5 }} /> BACK
            </BackOrButton>
          </div>

          <Typography variant="h1">Sub-Tray Details</Typography>
        </div>
        {!loading && !error && subTrayItem && (
          <div className="content-container">
            {/* Reference Panel */}
            <div className="reference-panel">
              <div className="panel-header">
                <Typography variant="h6">{referenceImageTitle}</Typography>
                {formattedTimestamp && (
                  <Typography variant="caption" className="timestamp">
                    {formattedTimestamp} | Sub-Tray ID:{' '}
                    <strong>{subTrayItem?.id}</strong> | Tray Type:{' '}
                    <strong>{trayDetails?.trayType}</strong>
                  </Typography>
                )}
              </div>

              <div className="instruction-text">
                <Button
                  className="capture-reference-button"
                  onClick={handleCaptureReferenceImage}
                  startIcon={<CameraAlt />}
                  size="small"
                  variant="outlined"
                >
                  {imageHistory.length > 0 ? 'Re-capture' : 'Capture'} Reference
                  Image
                </Button>
              </div>

              <div className="reference-image-container">
                {!subTrayItem || loading || error || isImageLoading ? (
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    className="skeleton"
                  />
                ) : (
                  <>
                    <img
                      src={trayImage}
                      alt={subTrayItem.description}
                      className="reference-image"
                      style={{
                        transform: `scale(${imageScale}) rotate(${imageRotation}deg)`,
                      }}
                      onClick={() => setIsImagePreviewOpen(true)}
                    />

                    {/* Image navigation controls for history */}
                    {hasMultipleImages && (
                      <div className="image-navigation-controls">
                        <button
                          onClick={goToPreviousImage}
                          disabled={
                            currentImageIndex >= imageHistory.length - 1
                          }
                          className="nav-button prev-button"
                          aria-label="Previous image"
                        >
                          <ChevronLeft />
                        </button>

                        <div className="image-counter">
                          {imageHistory.length - currentImageIndex} /{' '}
                          {imageHistory.length}
                        </div>

                        <button
                          onClick={goToNextImage}
                          disabled={currentImageIndex <= 0}
                          className="nav-button next-button"
                          aria-label="Next image"
                        >
                          <ChevronRight />
                        </button>
                      </div>
                    )}

                    {/* Latest image button */}
                    {isViewingHistory && (
                      <Tooltip title="Go to latest image">
                        <button
                          onClick={goToLatestImage}
                          className="latest-image-button"
                          aria-label="Go to latest image"
                        >
                          <Update /> <span>Return To Latest</span>
                        </button>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>

              <div className="reference-panel-controls">
                <button
                  onClick={handleZoomOut}
                  disabled={imageScale <= 0.5}
                  aria-label="Zoom out"
                >
                  <ZoomOut />
                </button>
                <button
                  onClick={handleZoomIn}
                  disabled={imageScale >= 3}
                  aria-label="Zoom in"
                >
                  <ZoomIn />
                </button>
                <button onClick={handleRotateImage} aria-label="Rotate image">
                  <RotateRight />
                </button>
              </div>
            </div>

            {/* Map Container */}
            {!isInstrumentTray && (
              <div className="map-container">
                <div className="panel-header">
                  <Typography variant="h6">Digital Tray Map</Typography>
                  <Button
                    className="legend-toggle-button"
                    onClick={() => setIsLegendOpen(true)}
                    aria-label="Show Legend"
                  >
                    <HelpOutline />
                  </Button>
                </div>

                <div className="instruction-text">
                  <div className="instruction-content">
                    <Typography variant="h6">
                      {isInEditMode
                        ? 'Edit Mode: Click on screws or plates to load them'
                        : 'View Mode: Click Edit to make changes'}
                    </Typography>
                  </div>
                  <div className="instruction-actions">
                    <Button
                      variant={isInEditMode ? 'contained' : 'outlined'}
                      onClick={handleEditModeChange}
                      className="mode-toggle-button"
                      disabled={isUpdating}
                      size="small"
                    >
                      {isInEditMode ? 'Exit Edit Mode' : 'Edit Tray'}
                    </Button>

                    {isInEditMode && (
                      <OtherItemsTriggerButton
                        buttonProps={{
                          className: 'load-other-items-button',
                        }}
                        trayType={
                          trayDetails?.trayType || 'stryker screw caddy'
                        }
                      />
                    )}

                    {isInEditMode && !isPlatesOnly && !isCustomImplantTray && (
                      <Button
                        variant={isLoadingWithUDIs ? 'contained' : 'outlined'}
                        onClick={handleLoadWithUDIsClick}
                        className="load-mode-button"
                        disabled={isUpdating}
                        size="small"
                      >
                        {isLoadingWithUDIs ? 'Load With DI' : 'Load With UDI'}
                      </Button>
                    )}

                    {isInEditMode && isCustomImplantTray && (
                      <Button
                        variant="contained"
                        className="load-mode-button"
                        disabled={isUpdating}
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddCustomImplantTrigger}
                      >
                        Add Custom Products
                      </Button>
                    )}

                    {isInEditMode && (
                      <LoadingButton
                        variant="contained"
                        onClick={() => handleConfirmationClick()}
                        className="confirm-button"
                        loading={isUpdating}
                        size="small"
                      >
                        Confirm
                      </LoadingButton>
                    )}
                  </div>
                </div>

                {/* Mode Banner */}
                {isInEditMode && !isPlatesOnly && (
                  <div
                    className={`mode-banner ${
                      isLoadingWithUDIs ? 'scan-mode' : 'click-mode'
                    }`}
                  >
                    <Typography variant="body1">
                      {isLoadingWithUDIs ? (
                        <>
                          <strong>Load With UDI:</strong> Select a screw or
                          plate location, then scan the asset to load it
                        </>
                      ) : (
                        <>
                          <strong>Load With DI:</strong> Click directly on a
                          screw or plate location to load an asset with
                          pre-indexed device identifiers
                        </>
                      )}
                    </Typography>
                  </div>
                )}

                <div className="tray-map_container">
                  <div
                    className={classNames('map-selector-container', {
                      disabled: !isInEditMode,
                    })}
                  >
                    <TrayMapSelector
                      isSPD={isLoadingWithUDIs}
                      trayType={trayDetails?.trayType || 'stryker screw caddy'}
                      trayImg={trayDetails?.trayImg}
                    />
                  </div>
                </div>

                {isInEditMode && !isCustomImplantTray && (
                  <div className="load-all-container">
                    {hasScrews && !isLoadingWithUDIs && (
                      <Button
                        disabled={isAllScrewsLoaded}
                        onClick={loadAllScrews}
                        variant="outlined"
                        className="load-all-button"
                      >
                        Load All Screws
                      </Button>
                    )}
                    {hasPlates && (
                      <Button
                        disabled={isAllPlatesLoaded}
                        onClick={() => increaseAllPlatesBy(1)}
                        variant="outlined"
                        className="load-all-button"
                      >
                        Increase All Plates
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loading && <CircularProgress className="loading-spinner" />}

        {!loading && error && (
          <Typography className="error" variant="body1">
            {error.message}
          </Typography>
        )}

        <ConfirmChangesModal
          open={isConfirmationOpen}
          modalText={
            isRejecting
              ? 'Are you sure you want to reject this tray?'
              : isReviewing
              ? 'Are you sure you want to approve this tray'
              : undefined
          }
          onClose={handleConfirmationClose}
          handleConfirm={handleUserConfirmation}
          handleSaveChangesCancellation={handleConfirmationClose}
        />

        <ConfirmLoadMultipleScrewsModal
          open={isAdditionalDeviceCountModalOpen}
          onClose={() => {
            return
          }}
        />

        {!loading &&
          !error &&
          subTrayItem &&
          !isInstrumentTray &&
          !isCustomImplantTray && (
            <div className="tray-contents-section contained-desktop">
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="tray-contents-content"
                  id="tray-contents-header"
                >
                  <div className="accordion-summary">
                    <Typography variant="h2">Tray Contents</Typography>
                    <Typography variant="body2">
                      {totalScrews} Screws, {totalPlateCount} Plates
                    </Typography>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="tray-contents-items">
                    {renderSubTrayPlates()}

                    {trayDetails?.screws.length === 0 &&
                      trayDetails?.plates.length === 0 && (
                        <Typography variant="body2">
                          No screws or plates in this tray
                        </Typography>
                      )}

                    {trayDetails?.screws.map((screw, index) => (
                      <TrayProduct
                        key={`${screw.deviceId}${index}`}
                        {...screw}
                        showDispositionStatus={false}
                      />
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          )}

        {/* Legend Overlay */}
        {isLegendOpen && (
          <div className="legend-overlay">
            <div className="legend-container">
              <div className="legend-header">
                <h3>Hardware Status Legend</h3>
                <button
                  className="close-legend-button"
                  onClick={() => setIsLegendOpen(false)}
                  aria-label="Close Legend"
                >
                  <Close />
                </button>
              </div>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-circle blue" />
                  <span>Loaded</span>
                </div>
                <div className="legend-item">
                  <div className="legend-circle orange" />
                  <span>Selected</span>
                </div>
                <div className="legend-item">
                  <div className="legend-circle wasted" />
                  <span>Wasted</span>
                </div>
                <div className="legend-item">
                  <div className="legend-circle not-loaded" />
                  <span>Not Loaded</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <CustomImplantModal
          open={isCustomImplantModalOpen}
          onClose={handleAddCustomImplantTrigger}
        />

        {/* Image Preview Dialog */}
        <ViewImageDialog
          open={isImagePreviewOpen}
          handleClose={() => setIsImagePreviewOpen(false)}
          trayImage={trayImage || ''}
        />

        {/* Reference Image Accurate Modal */}
        <ReferenceImageAccurateModal
          open={isReferenceImageAccurateModalOpen}
          onClose={() => setIsReferenceImageAccurateModalOpen(false)}
          handleConfirm={handleReferenceImageAccurate}
          handleCaptureNew={handleCaptureNewReferenceImage}
          imageUrl={trayImage}
        />
      </div>
    </OtherTrayItemsContextProvider>
  )
}

export default EditTray
